import React from 'react';
import {TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import {SetupWorkerState} from '../../model/workerState';
import {updateCaptionDataState} from '../../context/captionDataProvider';
import {updateConversationsFromServer} from '../../context/conversationsProvider';
import captionApi from '../../service/captionApi';
import ConversationsTab from './conversations/ConversationsTab';
import ConversationSettings from './conversations/ConversationSettings';
import MediaPrivacySettings from './privacy/MediaPrivacySettings';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {mediaStore} from '../../context/mediaProvider';
import {useTscTheme} from '../../context/themeProvider';
import config from '../../service/config';
import styled from 'styled-components';
import Captions from './accessibility/Captions';
import AudioDescriptions from './accessibility/AudioDescriptions';
import MediaSettings from './privacy/MediaSettings';
import logging from '../../service/logging';
import {MediaPrivacy} from '../../model/mediaDetails';

const SettingsSeparator = styled.hr<{theme: TscThemeNames}>`    
   border: none;
   border-top: 2px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.medium};
   width: calc(100% - 24px);
`;

export const SettingsTabBase: React.FC<IStateMappedProps> = ({IsVideo, Hash, MediaId, IsReady, IsYouTube, Privacy}) => {
   const theme = useTscTheme();
   const [loadingConversationData, setLoadingConversationData] = SetupWorkerState();
   const [loadingCaptionData, setLoadingCaptionData] = SetupWorkerState();
   const showAccessPhraseOptions = config.featureSwitches.EnableMediaAccessPhrase && Privacy !== MediaPrivacy.private;
   const showMediaSettings = config.watchConfig.viewersCanDownloadMedia || IsVideo || showAccessPhraseOptions;

   React.useEffect(() => {
      const getCaptioningStatus = async () => {
         setLoadingCaptionData({isWorking: true, hasError: false});
         try {
            updateCaptionDataState(await captionApi.getStatus(MediaId));
            setLoadingCaptionData({isWorking: false, hasError: false});
         } catch (e) {
            logging.error(`Failed to retrieve caption state for media ID: ${MediaId}`, e);
            setLoadingCaptionData({isWorking: false, hasError: true});
         }
      };

      const getConversationData = async () => {
         try {
            setLoadingConversationData({isWorking: true, hasError: false});
            await updateConversationsFromServer(Hash);
            setLoadingConversationData({isWorking: false, hasError: false});
         } catch {
            setLoadingConversationData({isWorking: false, hasError: true});
         }
      };
      if (IsReady) {
         void getConversationData();
         if (config.watchConfig.isOwnerOrAdmin && IsVideo && !IsYouTube) {
            void getCaptioningStatus();
         }
      }
   }, [IsReady]);

   return (
      <>
         <MediaPrivacySettings />
         <SettingsSeparator theme={theme} />
         {showMediaSettings && (
            <>
               <MediaSettings />
               <SettingsSeparator theme={theme} />
            </>
         )}
         {IsVideo && (
            <>
               <Captions
                  isWorking={loadingCaptionData.isWorking}
                  hasError={loadingCaptionData.hasError}
               />
               <SettingsSeparator theme={theme} />
               <AudioDescriptions />
               <SettingsSeparator theme={theme} />
            </>
         )}
         {!config.featureSwitches.UseSingleConversation && (
            <ConversationsTab
               isWorking={loadingConversationData.isWorking}
               hasError={loadingConversationData.hasError}
            />
         )}
         {config.featureSwitches.UseSingleConversation && (
            <ConversationSettings
               isWorking={loadingConversationData.isWorking}
               hasError={loadingConversationData.hasError}
            />
         )}
      </>
   );
};

export interface IStateMappedProps {
   IsVideo: boolean;
   Hash: string;
   MediaId: string;
   IsReady: boolean;
   IsYouTube: boolean;
   Privacy: MediaPrivacy;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {IsVideo, Hash, MediaId, IsReady, IsYouTube, Privacy}: Partial<IStateMappedProps>
): IStateMappedProps => ({IsVideo, Hash, MediaId, IsReady, IsYouTube, Privacy});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(SettingsTabBase);
